import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useEditorBundle, useBundleRoot } from '@/hooks/use-editor-bundle';
import { useCapabilityList } from '@/hooks/use-entity-list';
import { useAuth } from '@/lib/auth';
import { useHeaderAction } from '@/lib/header-action-context';
import { useCapabilityActions } from '@/hooks/use-capability-actions';
import { CapabilityDetailLayout, WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { api } from '@/lib/api-client';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Pencil } from 'lucide-react';
import { CapabilityHeaderActions } from '@/components/capability-detail/CapabilityHeaderActions';
import { toast } from 'sonner';
import { logger } from '@/lib/telemetry';
import type { Agent } from '@/types/admin';
import type { AvailableResources } from '@/types/agent-graph';
import { Card } from '@/components/ui/card';
import { AgentConfigTab } from '@/components/agent-config/AgentConfigTab';
import type { DeferredOverviewDirtyPayload } from '@/components/capability-detail/CapabilityOverviewTab';
import { TiledWindowProvider, TiledWindowContainer, useTiledWindows } from '@/components/tiled-windows';
import type { TiledWindowEntityType } from '@/types/tiled-window';

function buildAgentCapabilityPutBody(params: {
    overviewPatch: Record<string, unknown>;
    configDirty: boolean;
    agent: Agent;
}): Record<string, unknown> | null {
    const body: Record<string, unknown> = {};
    if (params.overviewPatch && Object.keys(params.overviewPatch).length > 0) {
        Object.assign(body, params.overviewPatch);
    }
    if (params.configDirty) {
        body.type_config = {
            model_ref: params.agent.model_ref ?? null,
            prompt_ref: params.agent.prompt_ref ?? null,
            tool_refs: params.agent.tool_refs ?? [],
            skill_refs: params.agent.skill_refs ?? [],
            memory_refs: params.agent.memory_refs ?? [],
        };
    }
    if (Object.keys(body).length === 0) return null;
    return body;
}

interface CapabilityEntity {
    id: string;
    name: string;
    app_key: string;
    fqcn?: string;
    type: string;
    description?: string | null;
    aliases?: string[];
    tags?: string[];
    status?: string;
    visibility?: string;
    content_hash?: string | null;
    config?: Record<string, unknown>;
    ext?: Record<string, unknown>;
    created?: string;
    modified?: string;
    working_revision?: {
        id?: string;
        version_string?: string;
        major?: number;
        minor?: number;
        patch?: number;
        type_config?: Record<string, unknown>;
    } | null;
    immutable_revisions?: {
        id?: string;
        version_string?: string;
        major?: number;
        minor?: number;
        patch?: number;
    }[];
}

interface ResourceItem {
    id: string;
    name: string;
    app_key?: string;
    aliases?: string[];
    description?: string | null;
    fqcn?: string;
    visibility?: string;
    created_by?: string;
    organization_id?: string;
    status?: string;
}

function extractAgentFromEntity(entity: CapabilityEntity): Agent {
    const manifest = entity.working_revision?.type_config ?? {};

    return {
        id: entity.id,
        name: entity.name,
        slug: entity.fqcn ?? entity.name,
        type: (manifest.type as string) ?? 'default',
        version: entity.working_revision?.version_string ?? '0.0.0',
        source: (manifest.source as string) ?? null,
        priority: (manifest.priority as number) ?? 0,
        description: entity.description ?? null,
        handler_class: (manifest.handler_class as string) ?? null,
        model_ref: (manifest.model_ref as string) ?? (manifest.model as string) ?? null,
        pre_processor_workflow: (manifest.pre_processor_workflow as string) ?? null,
        post_processor_workflow: (manifest.post_processor_workflow as string) ?? null,
        prompt_ref: (manifest.prompt_ref as string) ?? (manifest.prompt as string) ?? null,
        tool_refs: (manifest.tool_refs as string[]) ?? (manifest.tools as string[]) ?? [],
        skill_refs: (manifest.skill_refs as string[]) ?? (manifest.skills as string[]) ?? [],
        roles: (manifest.roles as string[]) ?? [],
        memory_refs: (manifest.memory_refs as string[]) ?? (manifest.memory as string[]) ?? [],
        prompts: (manifest.prompts as string[]) ?? [],
        capabilities: (manifest.capabilities as Record<string, unknown>) ?? null,
        metadata: (manifest.metadata ?? entity.ext ?? {}) as Record<string, unknown>,
        tags: entity.tags ?? [],
        visibility: (entity.visibility as Agent['visibility']) ?? 'account',
        is_active: entity.status === 'active',
        status: entity.status as Agent['status'],
        content_hash: entity.content_hash ?? null,
        created: entity.created ?? '',
        modified: entity.modified ?? '',
    };
}

function extractAgentFromRevision(
    data: unknown,
    revision: { id: string; type_config?: Record<string, unknown> },
): Agent {
    const entity = data as CapabilityEntity;
    const manifest = revision.type_config ?? {};

    return {
        id: entity.id,
        name: entity.name,
        slug: entity.fqcn ?? entity.name,
        type: (manifest.type as string) ?? 'default',
        version: '0.0.0',
        source: (manifest.source as string) ?? null,
        priority: (manifest.priority as number) ?? 0,
        description: entity.description ?? null,
        handler_class: (manifest.handler_class as string) ?? null,
        model_ref: (manifest.model_ref as string) ?? (manifest.model as string) ?? null,
        pre_processor_workflow: (manifest.pre_processor_workflow as string) ?? null,
        post_processor_workflow: (manifest.post_processor_workflow as string) ?? null,
        prompt_ref: (manifest.prompt_ref as string) ?? (manifest.prompt as string) ?? null,
        tool_refs: (manifest.tool_refs as string[]) ?? (manifest.tools as string[]) ?? [],
        skill_refs: (manifest.skill_refs as string[]) ?? (manifest.skills as string[]) ?? [],
        roles: (manifest.roles as string[]) ?? [],
        memory_refs: (manifest.memory_refs as string[]) ?? (manifest.memory as string[]) ?? [],
        prompts: (manifest.prompts as string[]) ?? [],
        capabilities: (manifest.capabilities as Record<string, unknown>) ?? null,
        metadata: (manifest.metadata ?? entity.ext ?? {}) as Record<string, unknown>,
        tags: entity.tags ?? [],
        visibility: (entity.visibility as Agent['visibility']) ?? 'account',
        is_active: entity.status === 'active',
        status: entity.status as Agent['status'],
        content_hash: entity.content_hash ?? null,
        created: entity.created ?? '',
        modified: entity.modified ?? '',
    };
}

const LOG_PREFIX = 'agent-detail-page';
const HEADER_LAYER_ID = 'agent-page';

export interface AgentDetailPageProps {
    appKey?: string;
    name?: string;
    headerLayerId?: string;
}

function AgentDetailPageInner({ appKey: appKeyProp, name: nameProp, headerLayerId }: AgentDetailPageProps = {}) {
    const params = useParams<{ appKey: string; name: string }>();
    const appKey = appKeyProp ?? params.appKey;
    const name = nameProp ?? params.name;
    const queryClient = useQueryClient();
    const { identity, currentOrganization } = useAuth();
    const { data: bundle, isLoading, refetchWithoutCache } = useEditorBundle(appKey!, name!);
    const data = useBundleRoot(bundle);
    const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
    const { openWindow } = useTiledWindows();
    const [configDirty, setConfigDirty] = useState(false);
    const [overviewPatch, setOverviewPatch] = useState<Record<string, unknown>>({});
    const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [includeAll, setIncludeAll] = useState(true);
    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
    const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;

    const { data: modelsData } = useCapabilityList(appKey, { type: 'model', per_page: 200 });
    const { data: promptsData } = useCapabilityList(appKey, { type: 'prompt', per_page: 200 });
    const { data: toolsData } = useCapabilityList(appKey, { per_page: 200 });
    const { data: skillsData } = useCapabilityList(appKey, { type: 'skill', per_page: 200 });
    const { data: rolesData } = useCapabilityList(appKey, { type: 'role', per_page: 200 });
    const { data: memoryData } = useCapabilityList(appKey, { type: 'memory', per_page: 200 });

    const { data: allModelsData } = useQuery({
        queryKey: ['capabilities', 'all', 'model'],
        queryFn: () => api.get('/capabilities', { params: { type: 'model', per_page: 500, include_entitled: 'true' } }).then((r) => r.data),
        enabled: includeAll && !!appKey,
    });
    const { data: allPromptsData } = useQuery({
        queryKey: ['capabilities', 'all', 'prompt'],
        queryFn: () => api.get('/capabilities', { params: { type: 'prompt', per_page: 500, include_entitled: 'true' } }).then((r) => r.data),
        enabled: includeAll && !!appKey,
    });
    const { data: allToolsData } = useQuery({
        queryKey: ['capabilities', 'all', 'all-types'],
        queryFn: () => api.get('/capabilities', { params: { per_page: 500, include_entitled: 'true' } }).then((r) => r.data),
        enabled: includeAll && !!appKey,
    });
    const { data: allSkillsData } = useQuery({
        queryKey: ['capabilities', 'all', 'skill'],
        queryFn: () => api.get('/capabilities', { params: { type: 'skill', per_page: 500, include_entitled: 'true' } }).then((r) => r.data),
        enabled: includeAll && !!appKey,
    });
    const { data: allMemoryData } = useQuery({
        queryKey: ['capabilities', 'all', 'memory'],
        queryFn: () => api.get('/capabilities', { params: { type: 'memory', per_page: 500, include_entitled: 'true' } }).then((r) => r.data),
        enabled: includeAll && !!appKey,
    });

    const immutableRevisions = useMemo(
        () => ((data as Record<string, unknown>)?.immutable_revisions ?? []) as Array<{
            id: string; type_config?: Record<string, unknown>;
        }>,
        [data],
    );

    const selectedImmutableRevision = useMemo(
        () => immutableRevisions.find((r) => r.id === selectedRevisionId),
        [immutableRevisions, selectedRevisionId],
    );

    const agent = useMemo<Agent | null>(() => {
        if (!data) return null;
        if (isViewingPublished && selectedImmutableRevision) {
            return extractAgentFromRevision(data, selectedImmutableRevision);
        }
        return extractAgentFromEntity(data as unknown as CapabilityEntity);
    }, [data, isViewingPublished, selectedImmutableRevision]);

    const toolsFromBundle = useMemo(() => {
        const refs = bundle?.localRefs ?? {};
        const items: (ResourceItem & { type?: string })[] = [];
        for (const [refId, cap] of Object.entries(refs)) {
            const c = cap as {
                type?: string;
                id?: string;
                name?: string;
                app_key?: string;
                fqcn?: string;
                description?: string | null;
                aliases?: string[];
            };
            if (c?.type) {
                const fqcn = c.fqcn ?? (c.app_key && c.name ? `${c.app_key}/${c.name}` : refId);
                items.push({
                    id: String(c.id ?? ''),
                    name: c.name ?? refId.split('/').pop() ?? refId,
                    app_key: c.app_key,
                    fqcn,
                    aliases: c.aliases ? [...c.aliases, fqcn] : [fqcn],
                    description: c.description ?? null,
                    type: c.type,
                });
            }
        }
        return items;
    }, [bundle?.localRefs]);

    const resources = useMemo<AvailableResources>(() => {
        const toResourceKey = (item: ResourceItem) =>
            item.fqcn ?? (item.app_key && item.name ? `${item.app_key}/${item.name}` : item.name);

        const mergeWithAll = <T extends ResourceItem>(
            appItems: T[],
            allItems: T[] | undefined,
            toOutput: (item: T) => {
                id?: number;
                name: string;
                aliases?: string[];
                description?: string;
                app_key?: string;
                fqcn?: string;
                provider?: string;
                type?: string;
                visibility?: string;
                created_by?: string;
                organization_id?: string;
                status?: string;
            },
        ) => {
            const seen = new Set<string>();
            const result: ReturnType<typeof toOutput>[] = [];
            for (const item of appItems) {
                const key = toResourceKey(item);
                if (!seen.has(key)) {
                    seen.add(key);
                    result.push(toOutput(item));
                }
            }
            if (includeAll && allItems) {
                for (const item of allItems as ResourceItem[]) {
                    const key = toResourceKey(item);
                    if (!seen.has(key)) {
                        seen.add(key);
                        result.push(toOutput(item as T));
                    }
                }
            }
            return result;
        };

        const listTools = (toolsData?.data ?? []) as (ResourceItem & { type?: string })[];
        const mergedTools = new Map<string, ResourceItem & { type?: string }>();
        for (const t of listTools) {
            const key = toResourceKey(t);
            if (!mergedTools.has(key)) mergedTools.set(key, t);
        }
        for (const t of toolsFromBundle) {
            const key = toResourceKey(t);
            if (!mergedTools.has(key)) mergedTools.set(key, t);
        }
        if (includeAll && allToolsData?.data) {
            for (const t of allToolsData.data as (ResourceItem & { type?: string })[]) {
                const key = toResourceKey(t);
                if (!mergedTools.has(key)) mergedTools.set(key, t);
            }
        }
        const allTools = Array.from(mergedTools.values());

        const appModels = (modelsData?.data ?? []) as ResourceItem[];
        const appPrompts = (promptsData?.data ?? []) as ResourceItem[];
        const appSkills = (skillsData?.data ?? []) as ResourceItem[];

        return {
            models: mergeWithAll(
                appModels,
                allModelsData?.data as ResourceItem[] | undefined,
                (m) => {
                    const fqcn = m.fqcn ?? (m.app_key && m.name ? `${m.app_key}/${m.name}` : undefined);
                    const aliases = [...(m.aliases ?? [])];
                    if (fqcn && !aliases.includes(fqcn)) aliases.push(fqcn);
                    return {
                        id: Number(m.id),
                        name: m.name,
                        aliases: aliases.length > 0 ? aliases : undefined,
                        description: m.description ?? undefined,
                        provider: (m as { provider?: string }).provider,
                        app_key: m.app_key,
                        fqcn,
                        visibility: m.visibility,
                        created_by: m.created_by,
                        organization_id: m.organization_id,
                        status: m.status,
                    };
                },
            ),
            prompts: mergeWithAll(
                appPrompts,
                allPromptsData?.data as ResourceItem[] | undefined,
                (p) => {
                    const fqcn = p.fqcn ?? (p.app_key && p.name ? `${p.app_key}/${p.name}` : undefined);
                    const aliases = [...(p.aliases ?? [])];
                    if (fqcn && !aliases.includes(fqcn)) aliases.push(fqcn);
                    return {
                        id: Number(p.id),
                        name: p.name,
                        aliases: aliases.length > 0 ? aliases : undefined,
                        description: p.description ?? undefined,
                        type: (p as { type?: string }).type,
                        app_key: p.app_key,
                        fqcn,
                        visibility: p.visibility,
                        created_by: p.created_by,
                        organization_id: p.organization_id,
                        status: p.status,
                    };
                },
            ),
            workflows: [],
            tools: allTools.map((t) => {
                const fqcn = t.fqcn ?? (t.app_key && t.name ? `${t.app_key}/${t.name}` : undefined);
                const aliases = [...(t.aliases ?? [])];
                if (fqcn && !aliases.includes(fqcn)) aliases.push(fqcn);
                return {
                    id: Number(t.id),
                    name: t.name,
                    aliases: aliases.length > 0 ? aliases : undefined,
                    description: t.description ?? undefined,
                    app_key: t.app_key,
                    fqcn: fqcn ?? undefined,
                    visibility: t.visibility,
                    created_by: t.created_by,
                    organization_id: t.organization_id,
                    status: t.status,
                    type: t.type,
                };
            }),
            skills: mergeWithAll(
                appSkills,
                allSkillsData?.data as ResourceItem[] | undefined,
                (s) => {
                    const fqcn = s.fqcn ?? (s.app_key && s.name ? `${s.app_key}/${s.name}` : undefined);
                    const aliases = [...(s.aliases ?? [])];
                    if (fqcn && !aliases.includes(fqcn)) aliases.push(fqcn);
                    return {
                        id: Number(s.id),
                        name: s.name,
                        aliases: aliases.length > 0 ? aliases : undefined,
                        description: s.description ?? undefined,
                        type: (s as { type?: string }).type,
                        app_key: s.app_key,
                        fqcn,
                        visibility: s.visibility,
                        created_by: s.created_by,
                        organization_id: s.organization_id,
                        status: s.status,
                    };
                },
            ),
            roles: ((rolesData?.data ?? []) as ResourceItem[]).map((r) => ({
                id: Number(r.id),
                name: r.name,
                aliases: r.aliases,
                description: r.description ?? undefined,
            })),
            memories: mergeWithAll(
                (memoryData?.data ?? []) as ResourceItem[],
                allMemoryData?.data as ResourceItem[] | undefined,
                (m) => {
                    const fqcn = m.fqcn ?? (m.app_key && m.name ? `${m.app_key}/${m.name}` : undefined);
                    const aliases = [...(m.aliases ?? [])];
                    if (fqcn && !aliases.includes(fqcn)) aliases.push(fqcn);
                    return {
                        id: Number(m.id),
                        name: m.name,
                        aliases: aliases.length > 0 ? aliases : undefined,
                        description: m.description ?? undefined,
                        type: (m as { type?: string }).type,
                        app_key: m.app_key,
                        fqcn,
                        visibility: m.visibility,
                        created_by: m.created_by,
                        organization_id: m.organization_id,
                        status: m.status,
                    };
                },
            ),
        };
    }, [
        modelsData,
        promptsData,
        toolsData,
        skillsData,
        rolesData,
        memoryData,
        toolsFromBundle,
        includeAll,
        allModelsData,
        allPromptsData,
        allToolsData,
        allSkillsData,
        allMemoryData,
    ]);

    const handleModelCardClick = useCallback((modelRef: string) => {
        const parts = modelRef.includes('/') ? modelRef.split('/', 2) : null;
        openWindow({
            entityType: 'model',
            entityId: modelRef,
            entityName: parts ? parts[1] : modelRef,
            appKey: parts ? parts[0] : appKey,
        });
    }, [openWindow, appKey]);

    const handlePromptCardClick = useCallback((promptRef: string) => {
        const parts = promptRef.includes('/') ? promptRef.split('/', 2) : null;
        openWindow({
            entityType: 'prompt',
            entityId: promptRef,
            entityName: parts ? parts[1] : promptRef,
            appKey: parts ? parts[0] : appKey,
        });
    }, [openWindow, appKey]);

    const handleToolCardClick = useCallback((toolRef: string, capType?: string) => {
        const capRef = toolRef.includes('::') ? toolRef.slice(0, toolRef.indexOf('::')) : toolRef;
        const parts = capRef.includes('/') ? capRef.split('/', 2) : null;
        openWindow({
            entityType: (capType || 'tool') as TiledWindowEntityType,
            entityId: toolRef,
            entityName: parts ? parts[1] : capRef,
            appKey: parts ? parts[0] : appKey,
        });
    }, [openWindow, appKey]);

    const handleOverviewDeferredDirtyChange = useCallback((payload: DeferredOverviewDirtyPayload) => {
        setOverviewPatch((prev) => {
            const next = payload.patch;
            if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
            return next;
        });
    }, []);

    const overviewDirty = Object.keys(overviewPatch).length > 0;
    const isDirty = overviewDirty || configDirty;

    const handleSave = useCallback(async () => {
        if (!appKey || !name || !agent || !isDirty) return;
        const agentToSave = currentAgent ?? agent;
        const body = buildAgentCapabilityPutBody({
            overviewPatch,
            configDirty,
            agent: agentToSave,
        });
        if (!body) return;
        setIsSaving(true);
        try {
            await api.put(`/applications/${appKey}/capabilities/${encodeURIComponent(name)}`, body);
            await refetchWithoutCache();
            queryClient.invalidateQueries({ queryKey: ['capability', appKey, name] });
            toast.success('Agent saved', { description: 'Agent settings saved to database.' });
            setConfigDirty(false);
            setOverviewPatch({});
            setCurrentAgent(null);
        } catch (err) {
            logger.error(`${LOG_PREFIX}.handleSave: failed`, { error: err });
            toast.error('Failed to save agent', {
                description: 'An unexpected error occurred. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    }, [
        appKey,
        name,
        agent,
        currentAgent,
        isDirty,
        overviewPatch,
        configDirty,
        queryClient,
        refetchWithoutCache,
    ]);

    const actions = useCapabilityActions({
        appKey: appKey!,
        name: name!,
        typeLabel: 'Agent',
        data: data as Record<string, unknown> | null,
        refetch: refetchWithoutCache,
        customSave: handleSave,
        customIsDirty: isDirty,
        customIsSaving: isSaving,
    });

    const saveRef = useRef(actions.save);
    saveRef.current = actions.save;
    const publishRef = useRef(actions.publish);
    publishRef.current = actions.publish;
    const editRef = useRef(actions.edit);
    editRef.current = actions.edit;

    const effectiveLayerId = headerLayerId ?? HEADER_LAYER_ID;

    useEffect(() => {
        if (!agent) return;
        const isArchived = agent.status === 'archived';
        if (isArchived) {
            removeHeaderLayer(effectiveLayerId);
            return;
        }
        pushHeaderLayer(effectiveLayerId, {
            action: (
                <CapabilityHeaderActions
                    typeLabel="Agent"
                    hasWorkingRevision={actions.hasWorkingRevision}
                    isViewingPublished={actions.isViewingPublished}
                    isArchived={isArchived}
                    selectedPublishedLabel={actions.selectedPublishedLabel}
                    isDirty={actions.isDirty}
                    isSaving={actions.isSaving}
                    isPublishing={actions.isPublishing}
                    onSave={() => void saveRef.current()}
                    onPublish={() => void publishRef.current()}
                    onEdit={() => editRef.current()}
                    onArchive={() => actions.openArchiveDialog()}
                    onSettings={() => actions.openSettings()}
                />
            ),
        });
        return () => removeHeaderLayer(effectiveLayerId);
    }, [agent, effectiveLayerId, actions.hasWorkingRevision, actions.isViewingPublished, actions.selectedPublishedLabel, actions.isDirty, actions.isSaving, actions.isPublishing, pushHeaderLayer, removeHeaderLayer]);

    if (isLoading) return <div className="p-6">Loading agent graph...</div>;
    if (!data || !agent) return <div className="p-6">Agent not found</div>;

    return (
        <>
            <TiledWindowContainer>
                <CapabilityDetailLayout
                    typeLabel="Agent"
                    appKey={appKey}
                    capabilityName={name}
                    entityData={data as Record<string, unknown> | undefined}
                    selectedRevisionId={selectedRevisionId}
                    onRevisionChange={setSelectedRevisionId}
                    onOverviewDeferredDirtyChange={handleOverviewDeferredDirtyChange}
                    pendingOverviewPatch={overviewPatch}
                    onSaveDeferred={() => void handleSave()}
                    isOverviewDirty={isDirty}
                    isSavingOverview={isSaving}
                    overviewFormDisabled={isSaving || !actions.hasWorkingRevision || isViewingPublished}
                >
                    <Card className="mt-6 border border-slate-200 bg-card p-6 text-card-foreground shadow-sm dark:border-slate-700">
                        <AgentConfigTab
                            agent={agent}
                            resources={resources}
                            readOnly={isSaving || !actions.hasWorkingRevision || isViewingPublished}
                            appKey={appKey}
                            onAgentChange={setCurrentAgent}
                            onDirtyChange={setConfigDirty}
                            onModelCardClick={handleModelCardClick}
                            onPromptCardClick={handlePromptCardClick}
                            onToolCardClick={handleToolCardClick}
                            currentUserId={identity?.sub}
                            currentOrgId={currentOrganization?.id}
                        />
                    </Card>
                </CapabilityDetailLayout>
            </TiledWindowContainer>
            <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Pencil size={18} className="text-amber-600" />
                            Replace working copy?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            A working copy already exists. Replacing it will discard any unsaved changes
                            and create a new working copy based on the selected published version.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={actions.handleConfirmReplace}>
                            Replace
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export default function AgentDetailPage(props: AgentDetailPageProps = {}) {
    return (
        <TiledWindowProvider>
            <AgentDetailPageInner {...props} />
        </TiledWindowProvider>
    );
}
